import React, { useState, useRef, useMemo } from "react";
import Dashboard from "../Layout/Dashboard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useParams, useNavigate } from "react-router-dom";
import {
  useBillingList,
  useBillingStats,
  useMedicalCenters,
} from "../hooks/queries"; // Assuming you have a custom hook for billing list
import { currencyFormat } from "../utils";
import PaymentDialog from "./PaymentDialog";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { BASE_URL } from "../constants";
import { Messages } from "primereact/messages";
import { Dropdown } from "primereact/dropdown";
import BillingHistoryDialog from "./BillingHistoryDialog";

const getMonthList = () => {
  const months = [];
  const start = new Date(2025, 0); // January 2025
  const now = new Date();
  // Set to first day of current month
  const last = new Date(now.getFullYear(), now.getMonth(), 1);
  let current = new Date(start);
  while (current < last) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    current.setMonth(current.getMonth() + 1);
  }
  return ["Todos", ...months.reverse()];
};

const ClientPaymentView = () => {
  const { clientId } = useParams();
  const toastRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = localStorage.getItem("isAdmin") === "true";

  const medicalCenters = useMedicalCenters({
    clientID: Number(clientId),
    enabled: isAdmin,
  });

  const monthList = useMemo(getMonthList, []);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [medicalCenter, setMedicalCenter] = useState(null);

  const billingStats = useBillingStats(
    { month: selectedMonth, client: Number(clientId) },
    {
      enabled: isAdmin,
    }
  );

  const billing = useBillingList({
    clientId,
    period: selectedMonth,
    medicalCenter,
  });

  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [payDialogVisible, setPayDialogVisible] = useState(false);
  const msgs = useRef(null);

  const [paymentDetails, setPaymentDetails] = useState({
    phoneCode: "0412",
    phoneNumber: "",
    amount: "",
    paymentDate: null,
    referenceLastDigits: "",
  });

  const hidePaymentDialog = () => {
    setPaymentDialogVisible(false);
  };
  const handleDialogSubmit = () => {
    console.log("Detalles del pago:", paymentDetails);
    // Aquí puedes agregar la lógica para procesar el pago
    setPaymentDialogVisible(false);
  };
  const handlePayment = (payment) => {
    navigate(`/pagos/detalle/${payment.id}`);
  };

  const paymentButtonTemplate = (rowData) => {
    return (
      <Button
        label="Pagar"
        icon="pi pi-dollar"
        onClick={() => handlePayment(rowData)}
      />
    );
  };
  const statusOptions = [
    { label: "Pendiente", value: "pending", severity: "danger" },
    { label: "En Revisión", value: "under_review", severity: "warning" },
    { label: "Pagado", value: "paid", severity: "success" },
  ];

  const getStatusTag = (status) => {
    const selectedStatus = statusOptions.find(
      (option) => option.value === status
    );
    return (
      <Tag severity={selectedStatus?.severity} value={selectedStatus?.label} />
    );
  };

  const paymentStatusTemplate = (rowData) => {
    return (
      <div
        className="cursor-pointer"
        onClick={() => {
          setSelectedPaymentId(Number(rowData.id));
          setShowHistoryDialog(true);
        }}
      >
        {getStatusTag(rowData.status)}
      </div>
    );
  };
  const onUpload = () => {
    toastRef.current.show({
      severity: "success",
      summary: "Éxito",
      detail: "Archivo subido con éxito",
    });
  };

  return (
    <Dashboard headerTitle="Detalle de pagos" backButtonUrl="/pagos/clientes">
      <div>
        <div class="flex flex-column pb-2 gap-2">
          <div className="flex justify-content-start align-items-center gap-4">
            <h2 className="my-0">Tabla de Pagos por Mes</h2>
            {isAdmin && (
              <>
                <div className="p-3 border border-round shadow-1 text-center">
                  <h3 className="mt-0 mb-1">Por Cobrar</h3>
                  {billingStats.isLoading ? (
                    <i className="pi pi-spinner pi-spin"></i>
                  ) : (
                    <span className="font-bold">
                      ${billingStats.data?.total_pending.toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="p-3 border border-round shadow-1 text-center">
                  <h3 className="mt-0 mb-1">Cobrado</h3>
                  {billingStats.isLoading ? (
                    <i className="pi pi-spinner pi-spin"></i>
                  ) : (
                    <span className="font-bold">
                      ${billingStats.data?.total_paid.toFixed(2)}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Dropdown
                placeholder="Seleccionar mes"
                className="h-fit w-3"
                options={monthList}
                value={selectedMonth}
                onChange={(e) => {
                  if (e.value === "Todos") {
                    setSelectedMonth(null);
                  } else {
                    setSelectedMonth(e.value);
                  }
                }}
              />
              <Dropdown
                placeholder="Seleccionar clínica"
                filterPlaceholder="Buscar clínica"
                filter
                className="w-5"
                options={medicalCenters.data || []}
                value={medicalCenter}
                onChange={(e) => setMedicalCenter(e.value)}
                optionLabel="name"
                optionValue="id"
                showClear
              />
            </div>
          )}
        </div>
        <DataTable
          value={billing.data?.data}
          lazy
          style={{ width: "100%" }}
          emptyMessage="No se hallaron pagos"
          loading={billing.isLoading}
        >
          <Column field="medical_center_name" header="Centro médico"></Column>
          <Column field="period" header="Mes"></Column>
          <Column
            field="amount_usd"
            header="Monto (REF)"
            body={(item) => currencyFormat(item.amount_usd)}
          ></Column>
          <Column body={paymentStatusTemplate} header="Estado"></Column>
          <Column body={paymentButtonTemplate} header="Acción"></Column>
          <Column
            field="pdf_report"
            header="Reporte PDF"
            body={(rowData) => (
              <Button
                label="Ver PDF"
                icon="pi pi-file"
                className="p-button-text"
                onClick={() => window.open(rowData.pdf_report, "_blank")}
              />
            )}
          ></Column>
          <Column
            field="invoice_pdf"
            header="Factura PDF"
            body={(rowData) => (
              <Button
                label="Ver Factura"
                icon="pi pi-file"
                className="p-button-text"
                onClick={() => window.open(rowData.invoice_pdf, "_blank")}
              />
            )}
          ></Column>
        </DataTable>
      </div>
      <Dialog
        visible={payDialogVisible}
        onHide={() => setPayDialogVisible(false)}
        style={{ width: "50vw" }}
        header={`Detalles del Pago - Periodo: ${paymentDetails.period}`}
        // modal
        footer={
          <Button
            label="Cerrar"
            icon="pi pi-times"
            onClick={() => setPayDialogVisible(false)}
            className="p-button-secondary"
          />
        }
      >
        <div className="p-field">
          <p className="text-2xl font-bold mt-0">
            Monto a pagar (USD): {currencyFormat(paymentDetails.amount)}
          </p>
          <p className="m-0 text-lg">
            <strong>Banco:</strong> <span>Mercantil (0105)</span>
          </p>
          <p className="font-bold text-lg">Pago móvil:</p>

          <div className="p-mb-2 flex">
            <div className="flex-auto">
              <p className="m-0">
                <strong>Número de Teléfono:</strong>{" "}
                <span>{"0412-3034004"}</span>
              </p>
            </div>
            <div className="w-3">
              <Button
                label="Copiar"
                icon="pi pi-copy"
                className="p-button-text"
                onClick={() => navigator.clipboard.writeText("0412-3034004")}
              />
            </div>
          </div>
          <div className="p-mb-2 flex ">
            <div className="flex-auto">
              <p className="m-0">
                <strong>RIF:</strong> <span>J-503151277</span>
              </p>
            </div>
            <div className="w-3">
              <Button
                label="Copiar"
                icon="pi pi-copy"
                className="p-button-text"
                onClick={() => navigator.clipboard.writeText("J-503151277")}
              />
            </div>
          </div>
          {/* <div className="p-mb-3c flex">
            <div className="flex-auto">
              <p className="m-0">
                <strong>Banco:</strong> 0105 <span>Mercantil</span>
              </p>
            </div>
            <div className="w-3">
              <Button
                label="Copiar"
                icon="pi pi-copy"
                className="p-button-text"
                onClick={() => navigator.clipboard.writeText("0105")}
              />
            </div>
          </div> */}
        </div>
        <div className="p-field">
          <p className="">
            <strong> Transferencia:</strong>
            <span> 01050113071113124164 </span>{" "}
          </p>
        </div>

        <div className="p-field">
          <p className="font-bold text-lg">Instrucciones:</p>
          <ol className="pl-4">
            <li>
              Debe de pagar el monto mostrado en USD a la tasa del día, el cuál
              será verificado usando la tasa del BCV de la fecha que aparece en
              el pago.
            </li>
            <li>
              Para verificar su pago el comprobante debe contener visible la
              fecha, referencia de la transacción, el monto.
            </li>
            <li>
              Evite retrasos en la confirmación de su pago para mantener su
              cuenta activa.
            </li>
          </ol>
        </div>

        <div className="p-field">
          <Messages ref={msgs} />
          <FileUpload
            name="payment_support"
            mode="basic"
            chooseLabel={"Subir Comprobante"}
            onSelect={(e) => {}}
            url={`${BASE_URL}insurance/medical-case-invoice/`}
            auto
            accept=".jpeg,.jpg,.png,.pdf"
            onBeforeUpload={({ formData, xhr }) => {
              //   formData.append("medical_case", data.id);

              const reqListener = (r) => {
                console.log("rrr", r);
                console.log(r.target.response);
                // convert the JSON string to a JSON object

                // setInvoice((prev) => [
                //   ...prev,
                //   {
                //     file: data.file,
                //     fileName: data.file_name,
                //     id: data.id,
                //   },
                // ]);
              };
              xhr.addEventListener("load", reqListener);
            }}
            onUpload={onUpload}
            onBeforeSend={(e) => {
              console.log("onBeforeSend", e);
            }}
            onValidationFail={(file) => {
              console.log("error", file);
              if (file.size > 1000000) {
                // msgs.current.show([
                //   {
                //     sticky: true,
                //     severity: "error",
                //     summary: "Tamaño de archivo inválido",
                //     detail: "El tamaño máximo permitido es 1MB",
                //   },
                // ]);
                // e.options.clear();
                return;
              }
            }}
            maxFileSize={100000} // 5MB
            invalidFileSizeMessageDetail="El tamaño máximo permitido es 1MB"
          />
        </div>
      </Dialog>

      <PaymentDialog
        visible={paymentDialogVisible}
        onHide={hidePaymentDialog}
        paymentDetails={paymentDetails}
        setPaymentDetails={setPaymentDetails}
        amountBs={0}
        onSubmit={handleDialogSubmit}
      />

      <BillingHistoryDialog
        billingId={selectedPaymentId}
        visible={showHistoryDialog}
        onHide={() => setShowHistoryDialog(false)}
        getStatusTag={getStatusTag}
      />
    </Dashboard>
  );
};

export default ClientPaymentView;
